import { LineDraft } from './jobLineDraft';
import { Job } from '../api/types';
import { getErrorCode } from '../utils/apiError';
import { classifyMutationFailure } from './jobMutation';
import { jobLineErrorMessage } from './routeUi';

export interface PendingLine {
  draft: LineDraft;
  /** Count of matching server lines immediately before the request; null means it was not sent (or was refused). */
  baselineCount: number | null;
  error?: string;
  /**
   * The server answered and refused the line (4xx), so it was certainly not
   * saved. Retrying unchanged would fail again: the user must fix it first
   * (e.g. enter a price override or set the property size) or remove it.
   */
  rejected?: boolean;
  /** Backend error code of the refusal, e.g. PROPERTY_SIZE_REQUIRED. */
  errorCode?: string;
}

/** Codes whose fix is on the property (its size). */
const PROPERTY_SIZE_CODES: readonly string[] = ['PROPERTY_SIZE_REQUIRED', 'UNIT_MISMATCH'];

export function needsPropertySizeFix(entry: Pick<PendingLine, 'errorCode'>): boolean {
  return Boolean(entry.errorCode && PROPERTY_SIZE_CODES.includes(entry.errorCode));
}

/**
 * Record a failed POST. A definite rejection was not saved, so its baseline
 * is dropped (the next retry sends it without a duplicate check); an
 * ambiguous failure keeps its baseline so a committed-but-unacknowledged
 * line is recognised on the next read and not sent twice.
 */
export function markPendingFailure(entry: PendingLine, err: unknown, fallback: string): PendingLine {
  // 409 ROUTE_CONFLICT (tracker item 57) gets a message saying how to fix it.
  const message = jobLineErrorMessage(err, fallback);
  if (classifyMutationFailure(err) === 'rejected') {
    const code = getErrorCode(err);
    return { ...entry, baselineCount: null, error: message, rejected: true, ...(code ? { errorCode: code } : {}) };
  }
  const { rejected: _rejected, errorCode: _code, ...rest } = entry;
  return { ...rest, error: message };
}

/** Set (or clear, with '') the manual price override on one pending line. */
export function setPendingOverride(entries: PendingLine[], key: string, unitPrice: string): PendingLine[] {
  return entries.map((entry) => (entry.draft.key === key ? { ...entry, draft: { ...entry.draft, unitPrice } } : entry));
}

/** Drop a pending line the user chose not to save. Only offered for rejected lines, which were never saved. */
export function removePendingLine(entries: PendingLine[], key: string): PendingLine[] {
  return entries.filter((entry) => entry.draft.key !== key);
}

export const pendingLineKey = (tenantId: string, jobId: string) => `job-pending-lines-${tenantId}-${jobId}`;
export const pendingCreateKey = (tenantId: string) => `job-create-current-${tenantId}`;
/** A POST may commit even when the browser never receives the response. */
export const uncertainCreateKey = (tenantId: string) => `job-create-uncertain-${tenantId}`;

function normalizedDecimal(value: string): string {
  const match = /^([+-]?)(\d+)(?:\.(\d+))?$/.exec(value.trim());
  if (!match) return value.trim();
  const whole = match[2].replace(/^0+(?=\d)/, '');
  const fraction = (match[3] || '').replace(/0+$/, '');
  const magnitude = fraction ? `${whole}.${fraction}` : whole;
  return match[1] === '-' && magnitude !== '0' ? `-${magnitude}` : magnitude;
}

/**
 * Server lines that look like this draft. A draft without an override is
 * priced by the backend (CHART or CATALOG), so any saved price matches it.
 */
export function matchingLineCount(job: Job, draft: LineDraft): number {
  return job.lineItems.filter((line) => line.itemId === draft.itemId && normalizedDecimal(line.quantity) === normalizedDecimal(draft.quantity) && line.pricingModel === draft.pricingModel && (draft.unitPrice === '' || normalizedDecimal(line.unitPrice) === normalizedDecimal(draft.unitPrice))).length;
}

/** A timed-out line POST may have succeeded. Only keep drafts still absent from the server. */
export function reconcilePendingLines(job: Job, entries: PendingLine[]): PendingLine[] {
  return entries.filter((entry) => entry.baselineCount === null || matchingLineCount(job, entry.draft) <= entry.baselineCount);
}

export function readPendingLines(tenantId: string, jobId: string): PendingLine[] {
  try {
    const parsed: unknown = JSON.parse(sessionStorage.getItem(pendingLineKey(tenantId, jobId)) || '[]');
    return Array.isArray(parsed) ? parsed.filter((entry): entry is PendingLine => Boolean(entry && typeof entry === 'object' && 'draft' in entry && entry.draft?.itemId)) : [];
  } catch { return []; }
}
